import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import {
  COLOR_SYSTEM,
  Flex,
  Static,
  Button,
  CheckBox,
  Input,
  Icon,
  Text,
  ToggleButton,
} from "components/atoms";
import { log, LogLevel } from "utils/LogUtil";
import { PRIVACY_POLICY_URL, SERVICE_TERMS_URL } from "common/constants";

const Title = styled.div`
  width: 100%;
  margin-bottom: 40px;
  text-align: center;
`;

const AgreementsTemplateFormLayout = styled.div`
  width: 500px;
  background: ${COLOR_SYSTEM.get("Gray")[0]};

  display: flex;
  flex-direction: column;
  padding: 28px;
  border-radius: 12px;
  margin: 0 auto;

  .form__title {
    margin-bottom: 40px;
  }

  .input-fields {
    margin-bottom: 100px;

    & > * + * {
      margin-top: 40px;
    }
  }

  .input-row {
    display: flex;
    gap: 24px;

    .input-field {
      flex: 1 1 33%;
    }
  }

  .input-multi-col {
    & > * + * {
      margin-top: 8px;
    }
  }

  .terms-box {
    border-radius: 8px;
    border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};

    margin-bottom: 100px;
  }

  .terms-agree-all {
    padding: 4px;
    border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
    cursor: pointer;
  }

  .terms {
    padding: 4px;
  }

  .terms-item {
    cursor: pointer;
  }

  .terms-link {
    cursor: pointer;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }

  .signup-btn {
    width: 50%;
    align-self: center;
  }
`;

interface AgreementsTemplateStyledProps {
  error?: boolean;
}

const DescriptionWrapper = styled.div<AgreementsTemplateStyledProps>`
  padding: 16px;
  margin-top: 13.5px;
  max-height: 200px;
  overflow-y: auto;
  border: ${({ error }) =>
    `1px solid ${error ? COLOR_SYSTEM.get("Red")?.[100] : COLOR_SYSTEM.get("Gray")?.[50]}`};
  border-radius: 12px;
  background-color: ${({ error }) =>
    error ? COLOR_SYSTEM.get("Red")?.[10] : COLOR_SYSTEM.get("Gray")?.[10]};

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    height: 36px;
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
    background-clip: padding-box;
    border: 1px solid transparent;
    border-radius: 24px;
  }

  &::-webkit-scrollbar-track-piece:end {
    background: transparent;
    margin-bottom: 10px;
  }

  &::-webkit-scrollbar-track-piece:start {
    background: transparent;
    margin-top: 10px;
  }
`;

const AttachCol = styled.div`
  & > * + * {
    margin-top: 12px;
  }
`;

const FileList = styled.ul`
  padding-inline-start: 0px;
  & > * + * {
    margin-top: 8px;
  }
`;

const FileItem = styled.li`
  padding: 12px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  .file-icon {
    width: 48px;
    height: 48px;
    background-color: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .file-info {
    flex: 1;
  }
`;

export type ErrorMessages =
  | "담당자 성함을 입력해주세요"
  | "전화번호를 잘못 입력하셨습니다."
  | "이메일 주소를 입력해주세요."
  | "유효한 이메일 주소가 아닙니다."
  | "이미 가입된 이메일 주소입니다."
  | "8글자 이상 입력해주세요."
  | "비밀번호가 일치하지 않습니다."
  | "기업명을 입력해주세요."
  | "사업자등록번호를 입력해주세요."
  | "사업자등록증을 첨부해주세요."
  | "입력시간이 초과되었습니다. 인증번호 재전송을 눌러 다시 진행해주세요."
  | "인증번호가 일치하지 않습니다."
  | "";

export type InputValueView = {
  password: boolean;
  passwordConfirm: boolean;
};

interface Props {
  onAgree: () => void;
  hasNext: boolean;
}

const AgreementsTemplate: React.FC<Props> = ({ onAgree, hasNext }) => {
  const [firstAgree, setFirstAgree] = useState(false);
  const [secondAgree, setSecondAgree] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // const { mutate: signUp, isLoading } = useSignUpMutation();
  log(LogLevel.VERBOSE, "AgreementsTemplate:render ");

  return (
    <Static customStyle={{ height: "100%", width: "100%" }}>
      <Flex
        customStyle={{
          height: "100%",
          minHeight: "600px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Flex
          customStyle={{
            marginTop: "100px",
            marginBottom: "60px",
            minWidth: "500px",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AgreementsTemplateFormLayout>
            <Text
              textType="LargeTitle2"
              color={COLOR_SYSTEM.get("Gray")?.[700]}
              className="form__title"
            >
              약관에 동의해주세요
            </Text>
            <div className="terms-box">
              <div className="terms-agree-all">
                <Flex 
                  alignItems="center"
                  onClick={() => {
                    setFirstAgree(!(firstAgree && secondAgree));
                    setSecondAgree(!(firstAgree && secondAgree));
                  }}
                >
                  <ToggleButton
                    color="Blue"
                    size="Large"
                    type="Icon"
                    variant="Ghost"
                    icon="Check Circle Filled"
                    active={firstAgree && secondAgree}
                    onClick={() => {
                      setFirstAgree(!(firstAgree && secondAgree));
                      setSecondAgree(!(firstAgree && secondAgree));
                    }}
                  />
                  <Text textType="InputLarge" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                    모두 동의합니다.
                  </Text>
                </Flex>
              </div>
              <div className="terms">
                <Flex 
                  alignItems="center" 
                  className="terms-item"
                  onClick={() => {
                    setFirstAgree(!firstAgree);
                  }}
                >
                  <ToggleButton
                    color="Blue"
                    size="Large"
                    type="Icon"
                    variant="Ghost"
                    icon="Check Circle Filled"
                    active={firstAgree}
                    onClick={() => {
                      setFirstAgree(!firstAgree);
                    }}
                  />
                  <Text textType="InputLarge" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                    [필수]{" "}
                    <Text
                      color={COLOR_SYSTEM.get("Blue")?.[400]}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(SERVICE_TERMS_URL, "_blank");
                      }}
                      className="terms-link"
                    >
                      약문약답 PhAI 서비스 이용약관
                    </Text>
                    에 동의합니다.
                  </Text>
                </Flex>
                <Flex 
                  alignItems="center" 
                  className="terms-item"
                  onClick={() => {
                    setSecondAgree(!secondAgree);
                  }}
                >
                  <ToggleButton
                    color="Blue"
                    size="Large"
                    type="Icon"
                    variant="Ghost"
                    icon="Check Circle Filled"
                    active={secondAgree}
                    onClick={() => {
                      setSecondAgree(!secondAgree);
                    }}
                  />
                  <Text textType="InputLarge" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                    [필수]{" "}
                    <Text
                      color={COLOR_SYSTEM.get("Blue")?.[400]}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(PRIVACY_POLICY_URL, "_blank");
                      }}
                      className="terms-link"
                    >
                      개인정보처리방침
                    </Text>
                    에 동의합니다.
                  </Text>
                </Flex>
              </div>
            </div>
            <Button
              color="Primary"
              size="Large"
              variant="Contained"
              type="Text"
              disabled={isLoading || !firstAgree || !secondAgree}
              disabledColor={isLoading || !firstAgree || !secondAgree}
              onClick={onAgree}
              className="signup-btn"
            >
              {hasNext ? "동의하고 다음 단계로" : "동의하기"}
            </Button>
          </AgreementsTemplateFormLayout>
        </Flex>
      </Flex>
    </Static>
  );
};

export default AgreementsTemplate;
